/**
 * The day's P&L — ROADMAP 6.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DAY'S MARK BASELINE IS NOT THE ENTRY PRICE
 *
 * Total MTM measures an open position from its entry average. The DAY's MTM
 * cannot: a position carried in from a previous session already earned or
 * lost everything up to yesterday's close, and that belongs to the days it
 * happened on. Marking it from the entry would report a week of P&L as
 * today's, every day, until it is closed.
 *
 *     opened THIS session   →  measured from its entry average
 *     carried in            →  measured from the PREVIOUS CLOSE
 *
 * A position can be both: carry two lots, add two more at lunch, and the
 * broker nets them into one row with one average that answers neither
 * question.
 *
 * SO THE DAY IS COMPUTED BY RE-FOLDING, SEEDED AT THE PREVIOUS CLOSE
 *
 * The opening position is fed into `applyFill` as though it had been bought
 * at yesterday's close, and only THIS session's fills are folded on top.
 * Closing a carried position then books the move since that close and
 * nothing earlier, which is what a day figure means — and the booked and
 * unbooked halves come out on the SAME weighted-average convention the rest
 * of the system realises on, rather than a second one invented here.
 *
 * A CONVENTION-FREE IDENTITY CHECKS IT
 *
 *     day gross = cash in from this session's fills
 *               + closingQty x ltp
 *               - openingQty x prevClose
 *
 * That form needs no cost-basis convention at all — no FIFO, no weighted
 * average, nothing to get wrong. It cannot produce the booked/unbooked split
 * a trader wants, which is why it is not what the module returns; it is what
 * a test asserts the two halves add up to.
 *
 * MARKS ARE THE CALLER'S, THE JOURNAL IS THE SERVER'S
 *
 * `ltp` and `prevClose` arrive from the browser's tick store, which has both
 * live per instrument. The quantities and the fills come from our own
 * journal. Neither side can supply the other's half, and this module is
 * where they meet — pure, so the meeting is testable without either.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import { applyFill, FLAT } from './positionProjection.js';
import { netPnl } from './pnl.js';
const round2 = (v) => Math.round(v * 100) / 100;
/**
 * One leg's day.
 *
 * The seed is the whole trick: an opening position entered at the previous
 * close. `applyFill` then treats a carried lot exactly as it treats one
 * bought this morning, and every rule it already enforces — weighted average
 * by size, realising on closed quantity only, crossing through zero — applies
 * to the day without being restated.
 */
export function legDay(leg) {
    const carried = leg.openingQty !== 0;
    if (carried && (leg.prevClose === null || !(leg.prevClose > 0))) {
        /*
         * A carried position with no previous close cannot have a DAY figure at
         * all — not its booked half either, because closing it today would book
         * against a baseline we do not have. Refused whole rather than reported
         * in part.
         */
        return {
            key: leg.key,
            booked: 0,
            unbooked: null,
            /*
             * The charges ARE known: they are on this session's fills and owe
             * nothing to a baseline. Zeroing them because the P&L is unknowable
             * would understate the day's cost by a real amount.
             */
            charges: round2(leg.fills.reduce((sum, f) => sum + Math.max(0, f.charges ?? 0), 0)),
            closingQty: leg.openingQty,
            marked: false,
            why: 'Carried in from a previous session with no closing price to measure the day from.',
        };
    }
    const seed = carried
        ? { ...FLAT, netQty: leg.openingQty, avgPrice: leg.prevClose }
        : FLAT;
    const state = [...leg.fills]
        .sort((a, b) => a.at - b.at)
        .reduce(applyFill, seed);
    const open = state.netQty !== 0;
    if (open && (leg.ltp === null || !(leg.ltp > 0))) {
        return {
            key: leg.key,
            booked: state.realisedPnl,
            unbooked: null,
            charges: state.chargesTotal,
            closingQty: state.netQty,
            marked: false,
            why: 'No price yet for the quantity still open.',
        };
    }
    return {
        key: leg.key,
        booked: state.realisedPnl,
        // A flat leg is marked at zero: an intraday round trip needs no price.
        unbooked: open ? round2((leg.ltp - state.avgPrice) * state.netQty) : 0,
        charges: state.chargesTotal,
        closingQty: state.netQty,
        marked: true,
        why: null,
    };
}
/**
 * The day, across every leg.
 *
 * `booked` and `charges` are always complete numbers: both come from fills,
 * which need no mark. Only `unbooked` can be partial, and `net` carries
 * exactly its completeness — the same rule `basketMtm` states, for the same
 * reason.
 *
 * ONE UNMARKED LEG DOES NOT ZERO THE DAY. It is counted and declared, so a
 * tile reading "+₹12,400 (3/4)" is honest about what it covers. Dropping the
 * leg silently is the failure; refusing the whole total when three quarters
 * of it is known is merely unhelpful.
 */
export function dayTotals(legs) {
    const computed = legs.map(legDay);
    let booked = 0;
    let charges = 0;
    let unbookedMarked = 0;
    let marked = 0;
    let unmarked = 0;
    for (const l of computed) {
        booked += l.booked;
        charges += l.charges;
        if (l.unbooked === null) {
            unmarked += 1;
            continue;
        }
        unbookedMarked += l.unbooked;
        marked += 1;
    }
    booked = round2(booked);
    charges = round2(charges);
    unbookedMarked = round2(unbookedMarked);
    const unbooked = totalOf(unbookedMarked, marked, unmarked);
    const net = unbooked.kind === 'UNMARKED'
        ? unbooked
        : // Through `netPnl`, so this does not become a second place charges
            // are subtracted — the failure 6.4's header describes.
            totalOf(netPnl(booked + unbooked.value, charges), marked, unmarked);
    return { legs: computed, booked, charges, unbooked, net, unmarkedLegs: unmarked };
}
function totalOf(value, marked, unmarked) {
    if (unmarked === 0)
        return { kind: 'COMPLETE', value };
    if (marked === 0)
        return { kind: 'UNMARKED', unmarked };
    return { kind: 'PARTIAL', value, marked, unmarked };
}
//# sourceMappingURL=dayTotals.js.map